import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  createSession,
  fetchHistory,
  fetchMission,
  deleteMission,
  forkMission,
  missionDownloadUrl,
  fetchMissionServers,
  setMissionServer,
  setMissionVisibility,
  type MissionView,
  type ServerRating,
  type RevisionView,
} from '../api/missions';
import { createServer, fetchPlayableServers, type PlayableServer } from '../api/servers';
import { fetchReadiness, fetchTerrains, type Readiness, type TerrainView } from '../api/catalog';
import { fetchSessions } from '../api/sessions';
import { ca } from '../i18n/ca';
import { QrCode } from './QrCode';
import { useAuth } from './AuthContext';
import { InvitedEditors } from './InvitedEditors';
import { useConfirm } from './useConfirm';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';
import { worldSizeOf } from './designModel';
import { MissionCover } from './MissionCover';
import { PlannedChip } from './Planned';
import { MissionMap } from './MissionMap';
import { MissionSides } from './MissionSides';
import { OverflowMenu } from './OverflowMenu';
import { fitOf, usable, withRating } from './serverFit';
import { ServerOptions } from './ServerOptions';
import { errorText } from './useSessionLive';
import { PreflightCard } from './PreflightCard';
import { GenerationNotice } from './GenerationNotice';
import { WarningLine } from './WarningLine';
import { fmtMoment } from './dates';

function isNight(hour: number | undefined): boolean | null {
  if (hour === undefined) return null;
  return hour < 6 || hour >= 20;
}

function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m} min`;
}

/** The mission page (UI doc 5.5): summary, map, objectives, cast, sessions, run and edit. */
export function MissionPage() {
  const { code = '' } = useParams();
  const { me } = useAuth();
  const navigate = useNavigate();
  const [mission, setMission] = useState<MissionView | null>(null);
  const [servers, setServers] = useState<PlayableServer[]>([]);
  const [creatingServer, setCreatingServer] = useState(false);
  const [ratings, setRatings] = useState<ServerRating[]>([]);
  /** The catalogue's terrains, for the one fact that decides a server: whose mod carries this world. */
  const [catalogTerrains, setCatalogTerrains] = useState<TerrainView[]>([]);
  const [history, setHistory] = useState<RevisionView[]>([]);
  const [serverId, setServerId] = useState<string>('');
  // Whether somebody has touched the select: the mission's own server is only
  // proposed until then.
  const [serverTouched, setServerTouched] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  // Whether the group's server already carries the mission's preset: the panel's own readiness.
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const [busyServers, setBusyServers] = useState<Set<string>>(new Set());
  const scheduleField = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setMission(await fetchMission(code));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [code]);

  const [attempt] = useRetryKey();
  useEffect(() => {
    if (me.authenticated) void load();
  }, [me.authenticated, load, attempt]);

  // The server the mission was made for is the one proposed (the owner,
  // 2026-09-18), while it is still one the platform may use; the select changes it.
  useEffect(() => {
    if (serverTouched || serverId !== '' || mission?.panelServerId == null) return;
    const own = servers.find((s) => s.id === mission.panelServerId);
    if (own && usable(own)) setServerId(String(own.id));
  }, [mission?.panelServerId, servers, serverTouched, serverId]);

  useEffect(() => {
    if (mission?.canEdit) {
      // Every server the panel lists, whatever the platform's access to it:
      // a mission can run on a server the platform only reads. Whoever may
      // edit the mission may make a session of it, so the list is the one an
      // editor may read, not the servers page's (2026-10-11).
      fetchPlayableServers()
        .then((r) => {
          setServers(r.servers);
        })
        .catch(() => setServers([]));
      // Which mod carries which world: the one fact that says a server cannot
      // run this mission at all, as opposed to running it with other mods.
      fetchTerrains()
        .then(setCatalogTerrains)
        .catch(() => setCatalogTerrains([]));
      fetchSessions(true)
        .then((r) =>
          setBusyServers(
            new Set(
              r.rows
                .filter((row) => row.state === 'LIVE' && row.serverName)
                .map((row) => row.serverName as string),
            ),
          ),
        )
        .catch(() => setBusyServers(new Set()));
    }
  }, [mission?.canEdit]);

  // Every server rated class by class, where the platform can say (editors only).
  useEffect(() => {
    if (mission?.canEdit && mission.code) {
      fetchMissionServers(mission.code)
        .then(setRatings)
        .catch(() => setRatings([]));
    }
  }, [mission?.canEdit, mission?.code]);

  // What the mission's own server runs today, when it still exists: which mods a
  // player has to carry is read off it.
  useEffect(() => {
    if (mission?.panelServerId && mission.server?.exists) {
      fetchReadiness(mission.panelServerId)
        .then(setReadiness)
        .catch(() => setReadiness(null));
    } else {
      setReadiness(null);
    }
  }, [mission?.panelServerId, mission?.server?.exists]);

  useEffect(() => {
    if (mission?.code) {
      fetchHistory(mission.code)
        .then(setHistory)
        .catch(() => setHistory([]));
    }
  }, [mission?.code]);

  async function run(scheduledAt: string | null) {
    if (!mission) return;
    setBusy(true);
    try {
      const session = await createSession(
        mission.code,
        serverId ? Number(serverId) : null,
        scheduledAt,
      );
      navigate(`/s/${session.session.code}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!mission) {
    return (
      <Shell title={ca.newMission}>
        {error ? (
          <PageProblem error={null} text={error} onRetry={() => void load()} />
        ) : (
          <PageLoading />
        )}
      </Shell>
    );
  }
  const def = mission.definition;
  const worldSize = worldSizeOf(def);
  const night = isNight(def.time?.hour);
  // A server that does not run the mission's mods cannot play it: it is listed,
  // named and disabled, rather than hidden, so "my server is not here" is never
  // a mystery. When none is left, the way out is to make one with the mission's own mods.
  const terrainModId =
    catalogTerrains.find((t) => t.worldName.toLowerCase() === def.terrain.worldName.toLowerCase())
      ?.modWorkshopId ?? null;
  const rated = servers.map((s) => ({
    server: s,
    fit: withRating(
      fitOf(s, def.modSet?.mods ?? [], terrainModId, mission.modSetHash),
      ratings.find((r) => r.serverId === s.id),
    ),
  }));
  const fitServers = rated.filter((r) => r.fit.fit === 'exact');
  const almostServers = rated.filter((r) => r.fit.fit === 'partial');
  const canManageServers = me.roles.includes('ADMIN') || me.roles.includes('SERVER_MANAGER');

  /**
   * A server made for this mission: the mods and Creator DLCs it was written
   * with, whatever has become of the server it was made for (2026-09-19). The
   * panel downloads what it no longer has and says so; the button is pressed
   * again when it has.
   */
  async function createForMission() {
    const mods = mission?.definition.modSet?.mods ?? [];
    if (!mission || mods.length === 0) return;
    setCreatingServer(true);
    setError(null);
    try {
      const created = await createServer({
        name: `${mission.title} · ${mission.code}`.slice(0, 48),
        mods: { ids: mods.map((m) => m.workshopId) },
        activeDlcs: mission.definition.modSet?.dlcs ?? [],
        keepForCampaign: false,
      });
      const refreshed = await fetchPlayableServers();
      setServers(refreshed.servers);
      setServerId(String(created.id));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setCreatingServer(false);
    }
  }
  const worldLabel = mission.worldDisplayName ?? def.terrain.worldName;
  // Null when nobody asked or the reader may not ask: an em dash, not "not scanned",
  // which would say the mod set was never looked at when we simply do not know.
  const origin = mission.server ?? null;
  const originState = !origin
    ? null
    : !origin.exists
      ? ca.missionUi.page.serverGone
      : origin.changed
        ? ca.missionUi.page.serverChanged(origin.added.length, origin.removed.length)
        : ca.missionUi.page.serverSame;
  // How much AI a night asks for, in the only unit the definition is exact about:
  // groups. `size` is how many men each holds and 0 means "the group template's
  // own number", which only the game knows, so the men are a floor, not a total.
  // Only a reader who may see `forces` has any of this: it is masked for players.
  const forces = def.forces ?? [];
  const aiGroups = forces.reduce((n, f) => n + (f.count ?? 1), 0);
  const aiMen = forces.reduce((n, f) => n + (f.count ?? 1) * (f.size ?? 0), 0);
  const endedSessions = mission.sessions.filter((s) => s.startedAt && s.endedAt);
  const avgDurationMs = endedSessions.length
    ? endedSessions.reduce(
        (sum, s) => sum + (new Date(s.endedAt!).getTime() - new Date(s.startedAt!).getTime()),
        0,
      ) / endedSessions.length
    : null;
  const achieved = mission.sessions.filter((s) => s.outcome === 'success').length;
  const decisionEntries = Object.entries(def.decisionMatrix ?? {});
  const shareUrl = `${window.location.origin}/m/${mission.code}`;
  // The platform refuses to delete a mission with a night ahead or under way (409); the page
  // says so up front instead of offering a button that can only fail.
  const hasOpenSession = mission.sessions.some(
    (s) => s.state === 'SCHEDULED' || s.state === 'LIVE',
  );

  async function removeMission() {
    const ask = ca.deleteMissionConfirm(mission!.title);
    if (!(await confirm({ ...ask, confirmLabel: ca.deleteMission, danger: true }))) return;
    setBusy(true);
    try {
      await deleteMission(mission!.code);
      navigate('/library');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell
      title={mission.title}
      kicker={
        mission.derivedFrom && (
          <>
            {ca.derivedFrom}{' '}
            <Link to={`/m/${mission.derivedFrom.code}`}>{mission.derivedFrom.title}</Link>
            {mission.derivedFrom.ownerHandle ? ` ${ca.by(mission.derivedFrom.ownerHandle)}` : ''}
          </>
        )
      }
      chips={
        <>
          {def.campaign && (
            <Link className="chip" to={`/c/${def.campaign.code}`}>
              {ca.campaign} · {ca.chapter(def.campaign.chapter)}
            </Link>
          )}
          <span className="chip">{worldLabel}</span>
          {mission.recipe && (
            <span className="chip">
              <i className={`bi bi-${mission.recipeIcon || 'flag'}`} aria-hidden="true" />
              {mission.recipeTitle ?? mission.recipe}
            </span>
          )}
          <span className="chip">{ca.revision(mission.revision)}</span>
        </>
      }
      actions={
        <>
          {mission.canEdit && (
            <>
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => void run(null)}
              >
                {ca.missionUi.page.playNow}
              </button>
              {/* The schedule fields stay in the "Executa-la" card; this takes the reader there. */}
              <button
                type="button"
                onClick={() => {
                  scheduleField.current?.scrollIntoView?.({ block: 'center' });
                  scheduleField.current?.focus();
                }}
              >
                {ca.missionUi.page.scheduleOpen}
              </button>
              <Link className="button" to={`/design/${mission.code}`}>
                {ca.edit}
              </Link>
            </>
          )}
          <OverflowMenu label={ca.missionUi.page.moreActions}>
            <button
              type="button"
              onClick={() =>
                void forkMission(mission.code)
                  .then((m) => navigate(`/m/${m.code}`))
                  .catch((e) => setError(errorText(e)))
              }
            >
              {ca.fork}
            </button>
            {mission.canDownload && (
              <>
                <a className="button" href={missionDownloadUrl(mission.code, 'zip')}>
                  <i className="bi bi-file-earmark-zip" aria-hidden="true" />
                  {ca.missionUi.page.downloadZip}
                </a>
                <a className="button" href={missionDownloadUrl(mission.code, 'pbo')}>
                  <i className="bi bi-box-seam" aria-hidden="true" />
                  {ca.missionUi.page.downloadPbo}
                </a>
              </>
            )}
            {mission.canEdit && (
              <button
                type="button"
                className="danger"
                disabled={busy || hasOpenSession}
                title={hasOpenSession ? ca.missionUi.page.deleteBlocked : undefined}
                onClick={() => void removeMission()}
              >
                {ca.deleteMission}
              </button>
            )}
          </OverflowMenu>
        </>
      }
    >
      {confirmDialog}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {/* Said once, under the header: an old mission costs a server wherever it is run from. */}
      <div className="mission-banner">
        <GenerationNotice
          code={mission.code}
          generation={mission.generation ?? null}
          onRegenerated={(m) => {
            setMission(m);
            void fetchHistory(m.code)
              .then(setHistory)
              .catch(() => undefined);
          }}
        />
      </div>
      <div className="split mission-page">
        <section className="cards">
          <article className="card">
            <MissionCover
              code={mission.code}
              revision={mission.revision}
              world={def.terrain.worldName}
              worldLabel={mission.worldDisplayName ?? def.terrain.worldName}
              recipe={mission.recipe}
              recipeIcon={mission.recipeIcon}
              recipeTitle={mission.recipeTitle ?? mission.recipe}
              cover={mission.cover ?? 'AUTO'}
              size="card"
              className="thumb page-cover"
            />
            {mission.canEdit && (
              <p className="muted small">
                <PlannedChip id="mission-own-cover" named />
              </p>
            )}
            <h2>{ca.summary}</h2>
            {def.warnings && def.warnings.length > 0 && (
              <div className="notice warn" role="alert">
                <strong>{ca.missionUi.page.warningsTitle}</strong>
                <ul>
                  {def.warnings.map((w) => (
                    <li key={w}>
                      <WarningLine code={w} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p>{def.summary}</p>
            <MissionSides sides={def.sides} modSetHash={mission.modSetHash} />
            <dl className="facts">
              <div>
                <dt>{ca.missionUi.page.terrain}</dt>
                <dd>{worldLabel}</dd>
              </div>
              <div>
                <dt>{ca.missionUi.page.recipe}</dt>
                <dd>
                  {mission.recipe ? (
                    <>
                      <i className={`bi bi-${mission.recipeIcon || 'flag'}`} aria-hidden="true" />{' '}
                      {mission.recipeTitle ?? mission.recipe}
                    </>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt>{ca.missionUi.page.night}</dt>
                <dd>
                  {night === null
                    ? '—'
                    : night
                      ? ca.missionUi.page.isNight
                      : ca.missionUi.page.isDay}
                </dd>
              </div>
              <div>
                <dt>{ca.missionUi.page.players}</dt>
                <dd>{def.sides.players.slots}</dd>
              </div>
              <div>
                <dt>{ca.missionUi.page.madeFor}</dt>
                <dd>{origin?.name ?? def.modSet?.serverName ?? '—'}</dd>
              </div>
              <div>
                <dt>{ca.missionUi.page.authors}</dt>
                <dd>
                  {mission.ownerHandle ?? '—'}
                  {mission.editors.length > 0
                    ? ` · ${ca.missionUi.page.andMoreEditors(mission.editors.length)}`
                    : ''}
                </dd>
              </div>
            </dl>
            <h3>{ca.missionUi.page.whoDecides}</h3>
            <p className="muted small">
              {decisionEntries.length === 0
                ? ca.missionUi.page.noDecisionsYet
                : decisionEntries
                    .map(([k, d]) => `${ca.decisionKeys[k] ?? k}: ${ca.decidedBy[d.by] ?? d.by}`)
                    .join(' · ')}
            </p>
          </article>
          <article className="card">
            <MissionMap
              world={def.terrain.worldName}
              worldSize={worldSize}
              objectives={def.objectives}
              start={def.sides.players.startPosition}
              area={def.area}
              markers={(def.markers ?? []).filter((m) => m.audience !== 'staff')}
              intel={def.intel}
              // As wide as its place and opened on the area of operations, like the session's
              // and the direction's (the owner, 2026-10-12); the rest of the terrain is a drag away.
              wide
              fit="area"
            />
          </article>
          <article className="card" aria-label={ca.objectives}>
            <h2>{ca.objectives}</h2>
            <ol className="plain numbered">
              {def.objectives.map((o) => (
                <li key={o.id}>
                  <strong>{o.priority === 'hidden' ? ca.hiddenObjective : o.title}</strong>{' '}
                  <span className="muted small">
                    {ca.priorityNames[o.priority ?? 'main']}
                    {o.silent && ` · ${ca.silentObjective}`}
                  </span>
                </li>
              ))}
            </ol>
          </article>
          <article className="card" aria-label={ca.cast}>
            <h2>{ca.cast}</h2>
            <ul className="plain">
              {(def.cast ?? []).map((c) => (
                <li key={c.id}>
                  <strong>{c.name}</strong> <span className="muted small">{c.role}</span>
                </li>
              ))}
            </ul>
          </article>
          <article className="card" aria-label={ca.supportsWhich}>
            <h2>{ca.supportsWhich}</h2>
            <ul className="plain">
              {Object.entries(def.supports ?? {})
                .filter(([, n]) => Number(n) > 0)
                .map(([k]) => (
                  <li key={k}>{ca.supportNames[k] ?? k}</li>
                ))}
            </ul>
            <p className="muted small">{ca.supportsNoCountHint}</p>
          </article>
        </section>
        <aside className="side">
          <section className="card" aria-label={ca.missionUi.page.timesPlayed}>
            <div className="two">
              <div>
                <p className="muted small">{ca.missionUi.page.timesPlayed}</p>
                <p className="num">{mission.sessions.length}</p>
              </div>
              <div>
                <p className="muted small">{ca.missionUi.page.achieved}</p>
                <p className="num">{achieved}</p>
              </div>
              <div>
                <p className="muted small">{ca.missionUi.page.avgDuration}</p>
                <p className="num">
                  {avgDurationMs === null ? '—' : formatDuration(avgDurationMs)}
                </p>
              </div>
              <div>
                <p className="muted small">{ca.missionUi.page.copies}</p>
                <p className="num">{mission.forks ?? 0}</p>
              </div>
            </div>
          </section>
          {mission.canEdit && <InvitedEditors code={mission.code} myId={me.id} />}
          {mission.canEdit && (
            <section className="card form" aria-label={ca.run}>
              <h2>{ca.run}</h2>
              {origin && (!origin.exists || origin.changed) && (
                <div className="notice warn small" role="status">
                  <p>
                    {origin.exists
                      ? ca.missionUi.page.serverChangedWarn(origin.name ?? '')
                      : ca.missionUi.page.serverGoneWarn(origin.name ?? '')}
                  </p>
                  {origin.added.length > 0 && (
                    <p>
                      {ca.missionUi.page.modsAdded} {origin.added.join(', ')}
                    </p>
                  )}
                  {origin.removed.length > 0 && (
                    <p>
                      {ca.missionUi.page.modsRemoved} {origin.removed.join(', ')}
                    </p>
                  )}
                  <p>{ca.missionUi.page.serverChangedHint}</p>
                </div>
              )}
              <label>
                {ca.runOn}
                <select
                  name="server"
                  value={serverId}
                  onChange={(e) => {
                    setServerTouched(true);
                    setServerId(e.target.value);
                    // The choice stays with the mission, so the next visit proposes it too.
                    void setMissionServer(
                      mission.code,
                      e.target.value ? Number(e.target.value) : null,
                    ).catch(() => undefined);
                  }}
                >
                  <option value="">—</option>
                  <ServerOptions rated={rated} busyServers={busyServers} />
                </select>
              </label>
              {fitServers.length === 0 && almostServers.length === 0 && (
                <p className="hint">
                  {ca.missionUi.page.noFitServer}
                  {canManageServers && (def.modSet?.mods.length ?? 0) > 0 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="link"
                        disabled={creatingServer}
                        onClick={() => void createForMission()}
                      >
                        {creatingServer
                          ? ca.missionUi.page.creatingServer
                          : ca.missionUi.page.createServer}
                      </button>
                    </>
                  )}
                </p>
              )}
              <label>
                {ca.missionUi.page.schedule}
                <input
                  ref={scheduleField}
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                />
              </label>
              <button
                type="button"
                disabled={busy || !scheduleAt}
                onClick={() => void run(scheduleAt ? new Date(scheduleAt).toISOString() : null)}
              >
                {ca.missionUi.page.scheduleGo}
              </button>
            </section>
          )}
          <section className="card form" aria-label={ca.missionUi.page.share}>
            <h2>{ca.missionUi.page.share}</h2>
            <p className="muted small">
              {ca.missionUi.page.shortCode}: <strong>{mission.code}</strong>
            </p>
            <div className="row">
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(shareUrl)
                    .then(() => setCopied(true))
                    .catch(() => undefined);
                }}
              >
                {copied ? ca.linkCopied : ca.copyLink}
              </button>
              <button type="button" onClick={() => setQr(!qr)} aria-pressed={qr}>
                {ca.qr}
              </button>
            </div>
            {qr && <QrCode value={shareUrl} label={ca.qrLabel} />}
            {mission.canEdit && (
              <label>
                {ca.visibility}
                <select
                  name="visibility"
                  value={mission.visibility}
                  onChange={(e) =>
                    void setMissionVisibility(
                      mission.code,
                      e.target.value as MissionView['visibility'],
                    )
                      .then(setMission)
                      .catch((err) => setError(errorText(err)))
                  }
                >
                  {Object.entries(ca.visibilityNames).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </section>
          <section className="card" aria-label={ca.missionUi.page.requirements}>
            <h2>{ca.missionUi.page.requirements}</h2>
            <dl className="facts">
              <dt>{ca.missionUi.page.madeFor}</dt>
              <dd>{origin?.name ?? def.modSet?.serverName ?? '—'}</dd>
              <dt>{ca.missionUi.page.madeForNow}</dt>
              <dd>{originState ?? '—'}</dd>
              <dt>{ca.missionUi.page.aiCost}</dt>
              <dd>{forces.length === 0 ? '—' : ca.missionUi.page.aiGroups(aiGroups, aiMen)}</dd>
            </dl>
            {readiness?.clientMods && readiness.clientMods.length > 0 && (
              <>
                <h3>{ca.missionUi.page.clientMods}</h3>
                <ul className="chips mod-chips">
                  {readiness.clientMods.map((m) => (
                    <li key={m} className="chip">
                      {m}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
          <PreflightCard mission={mission} />
          <section className="card" aria-label={ca.missionUi.page.versions}>
            <h2>{ca.missionUi.page.versions}</h2>
            <p className="muted small">{ca.missionUi.page.versionsHint}</p>
            {history.length > 0 && (
              <ol className="plain small versions" reversed>
                {[...history]
                  .sort((a, b) => b.revision - a.revision)
                  .slice(0, 8)
                  .map((r) => (
                    <li key={r.revision}>
                      <strong>r{r.revision}</strong> ·{' '}
                      {r.authorHandle ||
                        (r.note?.startsWith('corrected before play')
                          ? ca.design.platformAuthor
                          : ca.missionUi.page.unknownAuthor)}{' '}
                      · {fmtMoment(r.createdAt)}
                      {r.note && <span className="muted"> · {r.note}</span>}
                    </li>
                  ))}
              </ol>
            )}
            {mission.canEdit && (
              <Link to={`/design/${mission.code}`}>
                {ca.missionUi.page.seeHistory} ({history.length})
              </Link>
            )}
          </section>
          <section className="card" aria-label={ca.sessions}>
            <h2>{ca.sessions}</h2>
            {mission.sessions.length === 0 && (
              <p className="muted small">{ca.missionUi.page.noSessionsYet}</p>
            )}
            <ul className="plain">
              {mission.sessions.map((s) => (
                <li key={s.code}>
                  <Link to={`/s/${s.code}`}>{s.code}</Link>{' '}
                  <span className="muted small">
                    {ca.sessionStates[s.state]} · {ca.revision(s.missionRevision)}
                    {s.outcome ? ` · ${s.outcome}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          {mission.canEdit && (
            // Also in "Més accions": the owner looked for it on the page and did not find it there.
            <section className="card danger-zone" aria-label={ca.deleteMission}>
              <h2>{ca.deleteMission}</h2>
              <p className="muted small">{ca.missionUi.page.deleteHint}</p>
              {hasOpenSession && (
                <p className="notice warn small" role="status">
                  <i className="bi bi-lock" aria-hidden="true" /> {ca.missionUi.page.deleteBlocked}
                </p>
              )}
              <div className="row actions">
                <span className="grow" />
                <button
                  type="button"
                  className="danger"
                  disabled={busy || hasOpenSession}
                  onClick={() => void removeMission()}
                >
                  <i className="bi bi-trash" aria-hidden="true" /> {ca.deleteMission}
                </button>
              </div>
            </section>
          )}
        </aside>
      </div>
    </Shell>
  );
}
